import React, { useState, useEffect, useMemo } from 'react'
import { formatNumber } from '@urbanx/agx-ui-components'
import {
  AgxRow,
  AgxTypography,
  AgxTextInput,
  AgxButton,
  AgxDivider,
  AgxModal,
  AgxTable,
  AgxCollapsible,
  useWindowSize,
  Breakpoints,
  SaleGoals,
} from '@urbanx/agx.ui-components2'
import { parseNumber } from 'utils/parseNumber'

interface GoalsModalProps {
  saveGoals: (saleGoals: SaleGoals) => void
  goals: SaleGoals | undefined
  setIsDialogOpen: (isDialogOpen: boolean) => void
}

const GoalsModal = ({ saveGoals, goals, setIsDialogOpen }: GoalsModalProps) => {
  const [gciGoal, setGciGoal] = useState(goals?.gciGoal ?? 0)
  const [averageSalePrice, setAverageSalePrice] = useState(
    goals?.averageSalePrice ?? 0
  )
  const [averageCommRate, setAverageCommRate] = useState(
    goals?.averageCommissionRate ?? 0
  )
  const [conversionRate, setConversionRate] = useState(
    goals?.averageConversionRate ?? 0
  )

  useEffect(() => {
    setGciGoal(goals?.gciGoal ?? 0)
    setAverageSalePrice(goals?.averageSalePrice ?? 0)
    setAverageCommRate(goals?.averageCommissionRate ?? 0)
    setConversionRate(goals?.averageConversionRate ?? 0)
  }, [goals])

  const handleSaveGoalsClick = async () => {
    const saleGoals: SaleGoals = {
      gciGoal: gciGoal,
      averageSalePrice: averageSalePrice,
      averageCommissionRate: averageCommRate,
      averageConversionRate: conversionRate,
    }

    saveGoals(saleGoals)
    setIsDialogOpen(false)
  }

  //Make calculations when ABT updates (from get request or user inputs)
  const calculatedGoals = useMemo(() => {
    if (
      gciGoal > 0 &&
      averageCommRate > 0 &&
      averageSalePrice > 0 &&
      conversionRate > 0
    ) {
      const averageFee = (averageSalePrice * averageCommRate) / 100
      const sales = gciGoal / averageFee
      const listings = Math.round((sales / conversionRate) * 100)
      const salesVolume = averageSalePrice * sales

      return {
        gci: gciGoal,
        averageFee,
        salesVolume,
        listings,
        sales,
      }
    }
    return {
      gci: 0,
      averageFee: 0,
      salesVolume: 0,
      listings: 0,
      sales: 0,
    }
  }, [gciGoal, averageSalePrice, averageCommRate, conversionRate])

  const windowSize = useWindowSize()
  const isMobileSize = windowSize === Breakpoints.Mobile

  const generateRows = (
    value: number | null | undefined,
    isCurrency: boolean = false
  ) => [
    { label: 'Year', value: formatValue(value, 1, isCurrency) },
    { label: 'Quarter', value: formatValue(value, 4, isCurrency) },
    { label: 'Month', value: formatValue(value, 12, isCurrency) },
  ]

  const MobileTargetContent = ({
    rows,
  }: {
    rows: { label: string; value: string }[]
  }) => (
    <>
      {rows.map((row, index) => (
        <React.Fragment key={index}>
          <AgxRow
            padding="spacing-xs"
            gap="spacing-md"
            justifyContent="space-between"
            alignItems="alignCenter"
          >
            <AgxTypography
              id="agx-accordion-label"
              size="small"
              type="body_ui_strong"
            >
              {row.label}
            </AgxTypography>
            <AgxTypography id="agx-accordion-value" size="small" type="body_ui">
              {row.value === '0' ? '-' : row.value}
            </AgxTypography>
          </AgxRow>
          {index < rows.length - 1 && <AgxDivider variant="horizontal" />}
        </React.Fragment>
      ))}
    </>
  )

  const metrics = [
    { label: 'GCI', key: 'gci', isCurrency: true },
    { label: 'Sales Volume', key: 'salesVolume', isCurrency: true },
    { label: 'Listings', key: 'listings', isCurrency: false },
    { label: 'Sales', key: 'sales', isCurrency: false },
  ]

  const formatValue = (
    val: number | null | undefined,
    divisor = 1,
    isCurrency = false
  ) => {
    const result = val ? val / divisor : 0
    if (!result || !Math.floor(result)) return '-'
    return `${isCurrency ? '$' : ''}${formatNumber(Math.floor(result).toString())}`
  }

  const tableColumns = ['Target', 'Year', 'Quarter', 'Month']

  const tableRows = metrics.map(({ label, key, isCurrency }) => {
    const value = calculatedGoals[key as keyof typeof calculatedGoals]
    return {
      Target: label,
      Year: formatValue(value, 1, isCurrency),
      Quarter: formatValue(value, 4, isCurrency),
      Month: formatValue(value, 12, isCurrency),
    }
  })

  const collapsibleItems = metrics.map(({ label, key, isCurrency }) => {
    const value = calculatedGoals[key as keyof typeof calculatedGoals]
    return {
      open: true,
      title: label,
      content: <MobileTargetContent rows={generateRows(value, isCurrency)} />,
    }
  })

  const modalContent = !isMobileSize ? (
    <>
      <AgxRow gap="spacing-3xl" padding={'xl'}>
        <AgxTextInput
          id="gciGoal"
          label="GCI"
          type="currency"
          onChange={(value) => setGciGoal(parseNumber(value))}
          required
          noDecimalPoints
          defaultValue={gciGoal.toString()}
          stretch
          hideOptionalText
        />
        <AgxTextInput
          id="avgSalePrice"
          label="Average Sale Price"
          type="currency"
          onChange={(value) => setAverageSalePrice(parseNumber(value))}
          required
          noDecimalPoints
          defaultValue={averageSalePrice.toString()}
          hideOptionalText
          stretch
        />
        <AgxTextInput
          id="avgCommRate"
          label="Average Comm rate"
          onChange={(value) => setAverageCommRate(parseNumber(value))}
          type="percent"
          hideOptionalText
          defaultValue={averageCommRate.toString()}
          stretch
        />
        <AgxTextInput
          id="conversionRate"
          type="percent"
          label="List:sell Ratio"
          onChange={(value) => setConversionRate(parseNumber(value))}
          hideOptionalText
          noDecimalPoints
          defaultValue={conversionRate.toString()}
          stretch
        />
        <AgxTextInput
          id="averageFee"
          label="Average Fee"
          hideOptionalText
          type="currency"
          disabled
          noDecimalPoints
          defaultValue={calculatedGoals.averageFee.toString()}
          stretch
        />
      </AgxRow>
      <AgxTable columns={tableColumns} rows={tableRows} />
    </>
  ) : (
    <>
      <AgxTextInput
        id="gciGoal"
        label="GCI"
        type="currency"
        onChange={(value) => setGciGoal(parseNumber(value))}
        required
        noDecimalPoints
        defaultValue={gciGoal.toString()}
        stretch
        hideOptionalText
      />
      <AgxTextInput
        id="avgSalePrice"
        label="Average Sale Price"
        type="currency"
        onChange={(value) => setAverageSalePrice(parseNumber(value))}
        required
        noDecimalPoints
        defaultValue={averageSalePrice.toString()}
        hideOptionalText
        stretch
      />
      <AgxRow gap="spacing-2xl" fill>
        <AgxTextInput
          id="avgCommRate"
          label="Average Comm rate"
          onChange={(value) => setAverageCommRate(parseNumber(value))}
          type="percent"
          hideOptionalText
          defaultValue={averageCommRate.toString()}
          stretch
        />
        <AgxTextInput
          id="conversionRate"
          type="percent"
          label="List:sell Ratio"
          onChange={(value) => setConversionRate(parseNumber(value))}
          hideOptionalText
          noDecimalPoints
          defaultValue={conversionRate.toString()}
          stretch
        />
      </AgxRow>
      <AgxTextInput
        id="averageFee"
        label="Average Fee"
        hideOptionalText
        type="currency"
        disabled
        noDecimalPoints
        defaultValue={calculatedGoals.averageFee.toString()}
        stretch
      />
      {collapsibleItems.map((item) => (
        <AgxCollapsible key={item.title} {...item} variant="withoutDivider" />
      ))}
    </>
  )

  return (
    <AgxModal
      title="Annual Goals"
      type="wide"
      variant="withoutDivider"
      setIsDialogOpen={setIsDialogOpen}
      content={modalContent}
      actionButtons={
        <AgxRow justifyContent="center" gap="spacing-lg" fill>
          <AgxButton
            id="cancel"
            variant="hollow"
            size="large"
            text="Cancel"
            onClick={() => setIsDialogOpen(false)}
          />
          <AgxButton
            id="saveGoals"
            size="large"
            variant="primary"
            text="Save Goals"
            onClick={() => {
              handleSaveGoalsClick()
              setIsDialogOpen(false)
            }}
          />
        </AgxRow>
      }
      actionButtonContainerStyle={'withDivider'}
    />
  )
}

export default GoalsModal
